import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { queryClient } from './lib/queryClient';
import PrivateRoute from './components/PrivateRoute';
import { AppShell } from './components/app-shell';
import Login from './pages/Login';
import Dashboard from './pages/DashboardSimple';
import Invoices from './pages/Invoices';
import UploadInvoice from './pages/UploadInvoice';
import InvoiceGenerator from './pages/InvoiceGenerator';
import Vacations from './pages/Vacations';
import NewVacationRequest from './pages/NewVacationRequest';
import VacationAdmin from './pages/VacationAdmin';
import People from './pages/People';
import PersonDetail from './pages/PersonDetail';
import OrganizationalRoles from './pages/OrganizationalRoles';
import Onboarding from './pages/Onboarding';
import NewOnboarding from './pages/NewOnboarding';
import SlackTesting from './pages/SlackTesting';
import AdminSettings from './pages/AdminSettings';
import MyProfile from './pages/MyProfile';
import SlackKudosReview from './pages/SlackKudosReview';
import OrgChart from './pages/OrgChart';
import IntegrationsSettings from './pages/IntegrationsSettings';
import NotificationTemplates from './pages/NotificationTemplates';
import PerformanceReviews from './pages/PerformanceReviews';
import PerformanceReviewDetail from './pages/PerformanceReviewDetail';
import PerformanceReviewEdit from './pages/PerformanceReviewEdit';
import PerformanceReviewTemplateSettings from './pages/PerformanceReviewTemplateSettings';

function AppContent() {
  const { user, signOut } = useAuth();

  // Redirect to new onboarding if user hasn't completed it
  // Redirect if onboardingCompleted is false or undefined (not explicitly true)
  const shouldRedirectToOnboarding = user && user.onboardingCompleted !== true;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/new-onboarding"
        element={
          <PrivateRoute>
            <NewOnboarding />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <Dashboard />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <Invoices />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/upload"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <UploadInvoice />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices/generate"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <InvoiceGenerator />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/vacations"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <Vacations />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/vacations/new"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <NewVacationRequest />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/vacations/admin"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <VacationAdmin />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/people"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <People />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/people/:id"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <PersonDetail />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/performance-reviews"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <PerformanceReviews />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/performance-reviews/:id"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <PerformanceReviewDetail />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/performance-reviews/:id/edit"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <PerformanceReviewEdit />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/performance-review-templates"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <PerformanceReviewTemplateSettings />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/org-chart"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <OrgChart />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/organizational-roles"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <OrganizationalRoles />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <Onboarding />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/slack-testing"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <SlackTesting />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <AdminSettings />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/slack-kudos-review"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <SlackKudosReview />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/notification-templates"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <NotificationTemplates />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/integrations"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <IntegrationsSettings />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route
        path="/my-profile"
        element={
          <PrivateRoute>
            <AppShell user={user} onSignOut={signOut}>
              <MyProfile />
            </AppShell>
          </PrivateRoute>
        }
      />
      <Route 
        path="/" 
        element={
          shouldRedirectToOnboarding ? (
            <Navigate to="/new-onboarding" replace />
          ) : (
            <Navigate to="/dashboard" replace />
          )
        } 
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
